// /ai_manifesto — the public Manifesto Explorer page. Renders the site
// Header + nav above the explorer's own page-level header so readers
// can navigate back to the rest of the dashboard. Mounted
// unconditionally by App.tsx; the manifesto-explorer feature flag is
// checked INSIDE this component so a 'hidden' status swaps the chat
// UI for an "unavailable" message instead of going blank.

import { useEffect, useState } from 'react';
import {
  useManifestoQuery,
  parseCompareData,
} from '../../lib/useManifestoQuery';
import type {
  ManifestoLanguage,
  ManifestoResponse,
  ManifestoError,
} from '../../lib/useManifestoQuery';
import { useFeatureFlags } from '../../lib/useFeatureFlags';
import Header from '../Header';
import type { CitationRef } from './Citation';
import LanguageToggle from './LanguageToggle';
import QueryBox from './QueryBox';
import SuggestedChips from './SuggestedChips';
import AnswerAsk from './AnswerAsk';
import AnswerCompare from './AnswerCompare';
import PdfDrawer from './PdfDrawer';
import MethodologyNotice from './MethodologyNotice';
import './ManifestoExplorer.css';

/** True if `?preview=1` is on the URL OR was stuck in localStorage by an
 *  earlier preview visit. Mirrors usePreviewBypass in FeatureSection.tsx
 *  but inlined here so this component doesn't depend on that wrapper. */
function isPreviewActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const onUrl =
      new URLSearchParams(window.location.search).get('preview') === '1';
    if (onUrl) return true;
    return window.localStorage.getItem('lm-preview-bypass') === '1';
  } catch {
    return false;
  }
}

const T = {
  subtitle: {
    en: 'Compare the PL and PN manifestos side by side',
    mt: 'Qabbel il-manifesti tal-PL u tal-PN ħdejn xulxin',
  },
  how: { en: 'How this works', mt: 'Kif taħdem' },
  loading: {
    en: 'Reading both manifestos…',
    mt: 'Qed naqra ż-żewġ manifesti…',
  },
  loadingSlow: {
    en: 'This may take a few seconds…',
    mt: "Dan jista' jieħu ftit sekondi…",
  },
  // Shown after ~15s — the first call of the day pays a cold-cache cost
  // (Opus 4.7 over ~500K tokens), so we tell readers honestly that it
  // takes longer rather than leaving them guessing.
  loadingLong: {
    en: 'Still reading — the first call of the day takes a little longer.',
    mt: "Għadu jaqra — l-ewwel mistoqsija tal-ġurnata tieħu ftit aktar fit-tul.",
  },
  disclaimer: {
    en: 'Generated by AI from the manifesto text. Always verify against the cited pages.',
    mt: "Iġġenerata mill-IA mit-test tal-manifest. Dejjem ivverifika mal-paġni ċċitati.",
  },
  retry: {
    en: (n: number) => `You can ask again in ${n}s.`,
    mt: (n: number) => `Tista' terġa' tistaqsi fi ${n}s.`,
  },
} as const;

type LoadingPhase = 'initial' | 'slow' | 'long';

function Loading({
  language,
  phase,
}: {
  language: ManifestoLanguage;
  phase: LoadingPhase;
}) {
  return (
    <div className="mf-loading" aria-live="polite" aria-busy="true">
      <span className="mf-loading__spinner" aria-hidden="true" />
      <span>
        {T.loading[language]}
        {phase === 'slow' && ` ${T.loadingSlow[language]}`}
        {phase === 'long' && ` ${T.loadingLong[language]}`}
      </span>
    </div>
  );
}

function RetryCountdown({
  seconds,
  language,
}: {
  seconds: number;
  language: ManifestoLanguage;
}) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const id = setInterval(() => {
      setLeft((n) => (n > 0 ? n - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);
  if (left <= 0) return null;
  return <p className="mf-error__retry">{T.retry[language](left)}</p>;
}

function ErrorView({
  error,
  language,
}: {
  error: ManifestoError;
  language: ManifestoLanguage;
}) {
  return (
    <div className="mf-error" role="alert">
      <p className="mf-error__msg">{error.message}</p>
      {typeof error.retryAfterSeconds === 'number' && (
        <RetryCountdown seconds={error.retryAfterSeconds} language={language} />
      )}
    </div>
  );
}

function Disclaimer({ language }: { language: ManifestoLanguage }) {
  return <p className="mf-disclaimer">{T.disclaimer[language]}</p>;
}

function ResultView({
  response,
  language,
  onOpenCitation,
}: {
  response: ManifestoResponse;
  language: ManifestoLanguage;
  onOpenCitation: (c: CitationRef) => void;
}) {
  // Render shape now comes from the server's `shape` field, which is
  // derived from the answer JSON-parseability. Reader doesn't pick.
  if (response.shape === 'comparative') {
    const data = parseCompareData(response.answer);
    if (data) {
      return (
        <div className="mf-answer">
          <AnswerCompare
            data={data}
            language={language}
            onOpenCitation={onOpenCitation}
          />
          <Disclaimer language={language} />
        </div>
      );
    }
  }
  return (
    <div className="mf-answer">
      <AnswerAsk text={response.answer} onOpenCitation={onOpenCitation} />
      <Disclaimer language={language} />
    </div>
  );
}

// Reads the initial question / language from the URL on first load.
// The homepage funnel (ManifestoPromptStrip) deep-links into the
// explorer this way with the question pre-filled and the run kicked
// off automatically. Safe defaults if anything is missing or out of
// range.
interface InitialFromUrl {
  question: string;
  language: ManifestoLanguage;
}
function readInitialFromUrl(): InitialFromUrl {
  if (typeof window === 'undefined') {
    return { question: '', language: 'en' };
  }
  const params = new URLSearchParams(window.location.search);
  const rawQ = params.get('q') ?? params.get('question') ?? '';
  const question = rawQ.slice(0, 1000).trim();
  const rawLang = params.get('lang');
  const language: ManifestoLanguage = rawLang === 'mt' ? 'mt' : 'en';
  return { question, language };
}

function ToolUnavailable({ language }: { language: ManifestoLanguage }) {
  const en = language === 'en';
  return (
    <>
      <div className="site-top">
        <Header />
      </div>
      <div className="mf-explorer">
        <div
          className="mf-explorer__inner"
          style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}
        >
          <h1 className="mf-head__title" style={{ marginBottom: 12 }}>
            {en ? 'Tool currently unavailable' : "L-għodda mhux disponibbli bħalissa"}
          </h1>
          <p className="mf-head__subtitle">
            {en
              ? 'The manifesto comparison tool is temporarily disabled by the editors. Please check back later.'
              : "L-għodda għat-tqabbil tal-manifesti hija temporanjament diżattivata mill-edituri. Jekk jogħġbok erġa ipprova aktar tard."}
          </p>
        </div>
      </div>
    </>
  );
}

/** A single turn in the conversation. User turns carry just the
 *  string the reader typed; assistant turns carry the full server
 *  response so we can render the right shape (comparative vs prose). */
type ChatTurn =
  | { role: 'user'; content: string }
  | { role: 'assistant'; response: ManifestoResponse };

const NEW_LABEL = { en: 'New conversation', mt: 'Konversazzjoni ġdida' } as const;
const YOU_LABEL = { en: 'You', mt: 'Int' } as const;
const TOOL_LABEL = { en: 'Manifesto comparison', mt: 'Tqabbil tal-manifesti' } as const;

export default function ManifestoExplorer() {
  // Emergency kill: if the manifesto-explorer flag is set to 'hidden'
  // in /admin/flags AND the visitor isn't in preview mode, render the
  // "unavailable" message instead of the chat.
  const flags = useFeatureFlags();
  const flag = flags['manifesto-explorer'];
  const flagHidden = !isPreviewActive() && flag?.status === 'hidden';

  const initial = readInitialFromUrl();
  const [language, setLanguage] = useState<ManifestoLanguage>(initial.language);
  const [question, setQuestion] = useState('');
  const [openCitation, setOpenCitation] = useState<CitationRef | null>(null);
  const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>('initial');
  /** The full conversation thread: alternating user + assistant turns,
   *  rendered top-to-bottom. Empty thread = nothing has been asked yet. */
  const [thread, setThread] = useState<ChatTurn[]>([]);
  /** The pending user turn shown while the API call is in flight. Kept
   *  separate from `thread` so we can roll it back cleanly on error. */
  const [pendingUserTurn, setPendingUserTurn] = useState<string | null>(null);
  const { state, submit } = useManifestoQuery();

  const loading = state.status === 'loading';

  useEffect(() => {
    if (!loading) {
      setLoadingPhase('initial');
      return;
    }
    const slowId = setTimeout(() => setLoadingPhase('slow'), 5000);
    const longId = setTimeout(() => setLoadingPhase('long'), 15000);
    return () => {
      clearTimeout(slowId);
      clearTimeout(longId);
    };
  }, [loading]);

  /** Build the API-shaped messages array from the local thread + a new
   *  user question. Assistant turns are flattened to their answer
   *  string (the raw JSON for comparative, or the prose). */
  function buildMessages(prev: ChatTurn[], newQuestion: string) {
    return [
      ...prev.map((t) =>
        t.role === 'user'
          ? { role: 'user' as const, content: t.content }
          : { role: 'assistant' as const, content: t.response.answer },
      ),
      { role: 'user' as const, content: newQuestion },
    ];
  }

  async function runQuery(q: string) {
    const trimmed = q.trim();
    if (!trimmed || loading) return;
    setPendingUserTurn(trimmed);
    setQuestion('');
    const result = await submit({
      messages: buildMessages(thread, trimmed),
      language,
    });
    if (result.status === 'success') {
      setThread((prev) => [
        ...prev,
        { role: 'user', content: trimmed },
        { role: 'assistant', response: result.response },
      ]);
    } else {
      // Keep the pending user turn visible alongside the error so the
      // reader can see what they asked. The thread itself stays
      // unchanged — on retry we'll resubmit with the same prior turns.
    }
    setPendingUserTurn(null);
  }

  function resetConversation() {
    setThread([]);
    setPendingUserTurn(null);
    setQuestion('');
  }

  // Auto-submit on first mount if the URL carried ?q=…
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initial.question) {
      void runQuery(initial.question);
    }
  }, []);

  if (flagHidden) {
    return <ToolUnavailable language={language} />;
  }

  const hasThread = thread.length > 0 || pendingUserTurn !== null;

  return (
    <>
      <div className="site-top">
        <Header />
      </div>
      <div className="mf-explorer">
        <div className="mf-explorer__inner">
          <header className="mf-head">
            <div className="mf-head__titles">
              <h1 className="mf-head__title">
                {language === 'mt' ? 'Qabbel il-Manifesti' : 'Compare the Manifestos'}
              </h1>
              <p className="mf-head__subtitle">{T.subtitle[language]}</p>
            </div>
            <div className="mf-head__controls">
              <LanguageToggle value={language} onChange={setLanguage} />
              <a className="mf-head__how" href="#methodology">
                {T.how[language]}
              </a>
            </div>
          </header>

          {/* Conversation thread — rendered above the input once any turn
              exists. Empty initial state shows the chips instead. */}
          {hasThread && (
            <div className="mf-thread">
              {thread.map((turn, i) =>
                turn.role === 'user' ? (
                  <div key={i} className="mf-turn mf-turn--user">
                    <div className="mf-turn__label">{YOU_LABEL[language]}</div>
                    <div className="mf-turn__bubble">{turn.content}</div>
                  </div>
                ) : (
                  <div key={i} className="mf-turn mf-turn--assistant">
                    <div className="mf-turn__label">{TOOL_LABEL[language]}</div>
                    <ResultView
                      response={turn.response}
                      language={language}
                      onOpenCitation={setOpenCitation}
                    />
                  </div>
                ),
              )}
              {pendingUserTurn !== null && (
                <div className="mf-turn mf-turn--user mf-turn--pending">
                  <div className="mf-turn__label">{YOU_LABEL[language]}</div>
                  <div className="mf-turn__bubble">{pendingUserTurn}</div>
                </div>
              )}
              {loading && (
                <div className="mf-turn mf-turn--assistant">
                  <Loading language={language} phase={loadingPhase} />
                </div>
              )}
              {state.status === 'error' && !loading && (
                <ErrorView error={state.error} language={language} />
              )}
              <div className="mf-thread__actions">
                <button
                  type="button"
                  className="mf-thread__new"
                  onClick={resetConversation}
                  disabled={loading}
                >
                  ↺ {NEW_LABEL[language]}
                </button>
              </div>
            </div>
          )}

          <QueryBox
            value={question}
            onChange={setQuestion}
            onSubmit={() => void runQuery(question)}
            language={language}
            loading={loading}
          />

          {/* Suggested chips only on the empty initial state — once a
              conversation is going they get in the way. */}
          {!hasThread && (
            <SuggestedChips
              language={language}
              disabled={loading}
              onPick={({ question: q }) => {
                setQuestion(q);
                void runQuery(q);
              }}
            />
          )}

          <MethodologyNotice language={language} />
        </div>

        <PdfDrawer cite={openCitation} onClose={() => setOpenCitation(null)} />
      </div>
    </>
  );
}
